import { AlertTriangle, ChevronRight, MessageSquare, Users } from "lucide-react"
import {
  MAX_PREVIEW_CONDITIONS,
  parseMessageTemplate,
  renderTemplate,
  renderTemplateWithPlaceholders,
  versionLength,
  wordingWithPlaceholders,
  type ForcedOutcomes,
  type ParsedMessage,
} from "@/lib/message-template"
import type { PreviewState } from "@/components/automations/use-audience-preview"
import {
  MESSAGE_LIMIT,
  type PreviewLengths,
  type PreviewUnreached,
  type SettingPreviewResult,
} from "@/types/automation"

/**
 * How many people this would reach, and what they would receive.
 *
 * The counts explain themselves: the gap between "matched" and "would be
 * texted" is always spelled out as opt-outs and missing mobile numbers, so a
 * number that looks too low is never a mystery.
 *
 * A message with conditions is shown once per version members would actually
 * get, each on a real member from that group, with the wording nobody here
 * reaches listed underneath -- so every wording is seen before anything sends,
 * and the number of cards never outgrows the audience.
 *
 * Every member's text is measured too (milestone 32): saving judges only the
 * fixed wording, and a text that comes out over the limit once a member's
 * details are filled in is not sent. The lengths of those details arrive from
 * the roster, and the wording is added to them here, as it is typed.
 */
export function AudiencePreview({
  message,
  preview,
  organizationName,
  language,
}: {
  message: string
  preview: PreviewState
  organizationName: string
  /**
   * Set for one language's version. The counts are left out — the English
   * preview already shows them — and every sample is a member who prefers it.
   */
  language?: string
}) {
  const { loading, result, error } = preview

  if (error) {
    return (
      <div className="callout callout-danger py-3">
        <p className="text-xs text-danger-display">{error}</p>
      </div>
    )
  }

  if (loading && !result) {
    return (
      <div className="callout py-3">
        <p className="text-xs text-ink-muted">Checking the roster…</p>
      </div>
    )
  }

  if (!result) return null

  if (!result.ok) {
    return (
      <div className="callout callout-signal py-3">
        <p className="text-xs text-ink-body">{result.error}</p>
      </div>
    )
  }

  const reach = result.reach_count ?? 0
  const template = parseMessageTemplate(message)

  return (
    <div className={`callout py-3 ${loading ? "opacity-60" : ""}`}>
      {language ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-body">
          <Users className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
          <span>Members who prefer {language} get this version.</span>
        </p>
      ) : (
        <>
          <p className="flex items-center gap-1.5 text-xs text-ink-body">
            <Users className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
            <span>
              <span className="text-ink-display">
                {reach.toLocaleString()} {reach === 1 ? "member" : "members"}
              </span>{" "}
              in {organizationName} would get this today.
            </span>
          </p>

          <Exclusions
            matched={result.matched_count ?? 0}
            optedOut={result.opted_out_count ?? 0}
            noMobile={result.no_mobile_count ?? 0}
          />
        </>
      )}

      {template.valid && result.lengths && (
        <LengthLine template={template} lengths={result.lengths} language={language} />
      )}

      {!template.valid ? (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-danger-display">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Fix the condition to see the preview. {template.error}</span>
        </p>
      ) : template.conditions.length === 0 ? (
        <PlainBody template={template} result={result} language={language} />
      ) : (
        <VersionBodies template={template} result={result} language={language} />
      )}
    </div>
  )
}

function PlainBody({
  template,
  result,
  language,
}: {
  template: ParsedMessage
  result: SettingPreviewResult
  language?: string
}) {
  const hasSample = Object.keys(result.sample_variables).length > 0
  const body = hasSample
    ? renderTemplate(template, result.sample_variables)
    : renderTemplateWithPlaceholders(template)
  const from = result.sample_label ? ` from ${result.sample_label}` : ""

  let note: string
  if (language) {
    note = hasSample
      ? `Previewed with a real member${from} who prefers ${language}.`
      : `No member in this audience prefers ${language} right now, so the variables are shown as placeholders.`
  } else {
    note = hasSample
      ? `Previewed with a real member${from}.`
      : "Nobody matches right now, so the variables are shown as placeholders."
  }

  // A placeholder's brackets are not anybody's name, so only a real member's
  // text is measured.
  return <Body body={body} note={hasSample ? `${note} ${lengthOf(body)}` : note} />
}

/**
 * One card per version of the message somebody here would get, each pinned on
 * every grouped condition so it reads exactly as the send would render it for
 * that group. Wording nobody here reaches is listed underneath, collapsed,
 * so it can still be proofread.
 */
function VersionBodies({
  template,
  result,
  language,
}: {
  template: ParsedMessage
  result: SettingPreviewResult
  language?: string
}) {
  const combinations = result.combinations ?? []
  const unreached = result.unreached ?? []

  // No condition the roster could group by -- they all test columns this
  // automation does not return -- so there is one version to show.
  if (combinations.length === 0 && unreached.length === 0) {
    return <PlainBody template={template} result={result} language={language} />
  }

  const prefers = language ? ` who prefer ${language}` : ""

  return (
    <div className="mt-3 space-y-3">
      {combinations.map((combination) => {
        const hasSample = Object.keys(combination.sample_variables).length > 0
        const body = hasSample
          ? renderTemplate(template, combination.sample_variables, combination.holds)
          : renderTemplateWithPlaceholders(template, combination.holds)
        const count = combination.member_count
        const who = `${count.toLocaleString()} ${count === 1 ? "member" : "members"}${prefers} ${count === 1 ? "gets" : "get"} this version.`
        const shown = hasSample
          ? ` Shown with a real member${combination.sample_label ? ` from ${combination.sample_label}` : ""}.`
          : " Shown with placeholders."

        return (
          <div key={Object.entries(combination.holds).map(([i, holds]) => `${i}${holds}`).join("-")}>
            <p className="pl-5 text-xs font-medium text-ink-display">
              {describeCombination(template, combination.holds)}
            </p>
            <Body
              body={body}
              note={`${who}${shown}${hasSample ? ` ${lengthOf(body)}` : ""}${overIn(template, result.lengths, combination.holds)}`}
            />
          </div>
        )
      })}

      {combinations.length === 0 && (
        <p className="flex items-start gap-1.5 text-xs text-ink-muted">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Nobody{prefers} would get this today, so every wording is listed below with
            placeholders.
          </span>
        </p>
      )}

      {unreached.length > 0 && (
        <Unreached template={template} unreached={unreached} open={combinations.length === 0} />
      )}

      {template.conditions.length > MAX_PREVIEW_CONDITIONS && (
        <p className="text-xs text-ink-muted">
          Only the first {MAX_PREVIEW_CONDITIONS} conditions are previewed. The rest are
          still decided for every member when it sends.
        </p>
      )}
    </div>
  )
}

function Unreached({
  template,
  unreached,
  open,
}: {
  template: ParsedMessage
  unreached: PreviewUnreached[]
  open: boolean
}) {
  return (
    <details className="group pl-5" open={open}>
      <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-ink-muted hover:text-ink-body [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" />
        Wordings nobody here gets ({unreached.length})
      </summary>
      <ul className="mt-2 space-y-2">
        {unreached.map(({ index, holds }) => {
          const condition = template.conditions.find((c) => c.index === index)
          if (!condition) return null

          const wording = holds ? condition.thenText : condition.elseText

          return (
            <li key={`${index}-${holds}`} className="text-xs">
              <p className="font-medium text-ink-display">
                {describeCombination(template, { [index]: holds })}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-ink-body">
                {wording === "" ? "Adds nothing." : wordingWithPlaceholders(wording)}
              </p>
            </li>
          )
        })}
      </ul>
    </details>
  )
}

/**
 * "When {state} is ‘AB’", or "When {state} is neither ‘AB’ nor ‘MB’" -- one
 * phrase per field, so two conditions on the same column read as one fact.
 */
function describeCombination(template: ParsedMessage, holds: Record<number, boolean>): string {
  const fields = new Map<string, { field: string; is: string[]; isNot: string[] }>()

  for (const [key, side] of Object.entries(holds)) {
    const condition = template.conditions.find((c) => c.index === Number(key))
    if (!condition) continue

    const entry = fields.get(condition.field.toLowerCase()) ?? {
      field: condition.field,
      is: [],
      isNot: [],
    }
    // "is" when this side is where the member's value equals the condition's.
    const equal = (condition.operator === "=") === side
    const list = equal ? entry.is : entry.isNot
    if (!list.includes(condition.value)) list.push(condition.value)
    fields.set(condition.field.toLowerCase(), entry)
  }

  const quoted = (value: string) => `‘${value}’`
  const phrases = [...fields.values()].map(({ field, is, isNot }) => {
    if (is.length > 0) return `{${field}} is ${quoted(is[0])}`
    if (isNot.length === 1) return `{${field}} is not ${quoted(isNot[0])}`
    if (isNot.length === 2) return `{${field}} is neither ${quoted(isNot[0])} nor ${quoted(isNot[1])}`
    return `{${field}} is none of ${isNot.slice(0, -1).map(quoted).join(", ")} or ${quoted(isNot[isNot.length - 1])}`
  })

  return `When ${phrases.join(" and ")}`
}

/** "57 / 300 characters." — one rendered text, counted the way the send counts. */
function lengthOf(body: string): string {
  return `${Array.from(body).length} / ${MESSAGE_LIMIT} characters.`
}

/** " 2 in this group would run over." — nothing when none would. */
function overIn(
  template: ParsedMessage,
  lengths: PreviewLengths | null,
  holds: Record<number, boolean>,
): string {
  const stats = lengths && measure(template, lengths, holds)
  if (!stats || stats.over === 0) return ""
  return ` ${stats.over.toLocaleString()} in this group would run over.`
}

/**
 * The longest text any member here would get, and how many would come out
 * over the limit — every member's, not just the sample's. Built from how long
 * each member's details are, so a version's own fixed text is added here and
 * the figures follow the wording as it is typed. `only` narrows it to one
 * version's members. Null when the details varied too much to be sent.
 */
function measure(
  template: ParsedMessage,
  lengths: PreviewLengths,
  only?: Record<number, boolean>,
): { longest: number; over: number } | null {
  if (!lengths.rows) return null

  const pinned = lengths.condition_indices.length
  const versions = new Map<string, ReturnType<typeof versionLength>>()
  let longest = 0
  let over = 0

  for (const row of lengths.rows) {
    const forced: ForcedOutcomes = {}
    lengths.condition_indices.forEach((index, i) => {
      forced[index] = row[i] === 1
    })
    if (only && Object.entries(only).some(([index, holds]) => forced[Number(index)] !== holds)) {
      continue
    }

    const key = row.slice(0, pinned).join("")
    let version = versions.get(key)
    if (!version) {
      version = versionLength(template, forced)
      versions.set(key, version)
    }

    const uses = version.uses
    const details = lengths.variables.reduce(
      (total, name, j) => total + (uses[name.toLowerCase()] ?? 0) * row[pinned + j],
      0,
    )
    const length = version.fixed + details
    longest = Math.max(longest, length)
    if (length > MESSAGE_LIMIT) over += row[pinned + lengths.variables.length]
  }

  return { longest, over }
}

function LengthLine({
  template,
  lengths,
  language,
}: {
  template: ParsedMessage
  lengths: PreviewLengths
  language?: string
}) {
  const stats = measure(template, lengths)

  if (!stats) {
    return (
      <p className="mt-1 pl-5 text-xs text-ink-muted">
        These members’ details vary too much to measure every text here. Any that come out
        over {MESSAGE_LIMIT} characters still aren’t sent.
      </p>
    )
  }

  if (stats.over > 0) {
    const who = stats.over === 1 ? "member’s text" : "members’ texts"
    return (
      <p className="mt-1 flex items-start gap-1.5 text-xs text-signal-display">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {stats.over.toLocaleString()} {who}
          {language ? ` in ${language}` : ""} would come out over {MESSAGE_LIMIT} characters
          and won’t be sent. The longest is {stats.longest.toLocaleString()}.
        </span>
      </p>
    )
  }

  return (
    <p className="mt-1 pl-5 text-xs text-ink-muted">
      The longest text {language ? `in ${language} ` : ""}today is{" "}
      {stats.longest.toLocaleString()} of {MESSAGE_LIMIT} characters.
    </p>
  )
}

function Body({ body, note }: { body: string; note: string }) {
  return (
    <div className="mt-1 flex items-start gap-1.5">
      <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted" />
      <div className="min-w-0">
        <p className="whitespace-pre-wrap break-words text-sm text-ink-display">
          {body || "—"}
        </p>
        <p className="mt-1 text-xs text-ink-muted">{note}</p>
      </div>
    </div>
  )
}

function Exclusions({
  matched,
  optedOut,
  noMobile,
}: {
  matched: number
  optedOut: number
  noMobile: number
}) {
  const reasons = [
    optedOut > 0 && `${optedOut.toLocaleString()} opted out`,
    noMobile > 0 && `${noMobile.toLocaleString()} with no mobile number`,
  ].filter(Boolean) as string[]

  if (reasons.length === 0) return null

  return (
    <p className="mt-1 pl-5 text-xs text-ink-muted">
      {matched.toLocaleString()} matched, minus {reasons.join(" and ")}.
    </p>
  )
}
